import { AlertCircle, MoreVerticalIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { PublicRecordingComment } from "@classview/shared-types";
import { useQueryClient } from "@tanstack/react-query";

import { useDeleteRecordingComment } from "../api/delete-recording-comment";
import { fetchRecordingCommentsKey } from "../api/fetch-recording-comments";

type CommentRowActionsProps = {
  commentId: string;
  setIsEditing: (isEditing: boolean) => void;
};

export function CommentRowActions(props: CommentRowActionsProps) {
  const { setIsEditing } = props;
  const queryClient = useQueryClient();
  const { id: recordingId } = useParams();
  const { organisation } = useOrganisation();
  const {
    mutateAsync: deleteComment,
    isPending,
    isSuccess,
    isError,
    error,
    reset,
  } = useDeleteRecordingComment({
    onSuccess: () => {
      queryClient.setQueryData(
        fetchRecordingCommentsKey({
          organisationId: organisation?.id ?? "",
          recordingId: recordingId ?? "",
        }),
        (oldData: PublicRecordingComment[]) => {
          const hasReplies = oldData.some(
            (comment) => comment.parentCommentId === props.commentId,
          );

          if (hasReplies) {
            return oldData.map((comment) => {
              if (comment.id === props.commentId) {
                return { ...comment, content: "[deleted]" };
              }
              return comment;
            });
          }

          return oldData.filter((comment) => comment.id !== props.commentId);
        },
      );
    },
  });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!isDeleting) {
      reset();
    }
  }, [isDeleting, reset]);

  const handleDelete = async () => {
    await deleteComment({
      orgId: organisation?.id ?? "",
      recordingId: recordingId ?? "",
      commentId: props.commentId,
    });
    setIsDeleting(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  return (
    <Dialog onOpenChange={setIsDeleting} open={isDeleting}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVerticalIcon className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleEdit}>Edit</DropdownMenuItem>

          <DialogTrigger asChild>
            <DropdownMenuItem className="text-destructive">
              Delete
            </DropdownMenuItem>
          </DialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Are you absolutely sure?</DialogTitle>
          <DialogDescription>
            This action cannot be undone. Are you sure you want to permanently
            delete this comment?
          </DialogDescription>
        </DialogHeader>
        {isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unexpected error</AlertTitle>
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="submit"
            variant="destructive"
            onClick={handleDelete}
            disabled={isPending || isSuccess}
            isLoading={isPending || isSuccess}
            isAnimated
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
